import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const ModulesContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

export const ModulesProvider = ({ children }) => {
  const [modules, setModules] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchModules = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API}/api/system/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModules(response.data.modules || {});
    } catch (error) {
      console.error('Error fetching modules:', error);
      // Set defaults if error
      setModules({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchModules();
    
    // Listen for storage changes (login/logout)
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          // Token added - fetch modules
          fetchModules();
        } else {
          // Token removed - clear modules
          setModules({});
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically for token changes (same tab)
    const checkToken = () => {
      const token = localStorage.getItem('token');
      if (token && Object.keys(modules).length === 0) {
        fetchModules();
      }
    };
    
    const interval = setInterval(checkToken, 500);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [fetchModules, modules]);

  // Check if a module is enabled
  const isModuleEnabled = (moduleKey) => {
    // If modules not loaded yet, return true to avoid flickering
    if (Object.keys(modules).length === 0) return true;
    if (!modules[moduleKey]) return true; // Default to enabled if not found
    return modules[moduleKey].enabled !== false;
  };

  // Refresh modules (call after update)
  const refreshModules = () => {
    fetchModules();
  };

  return (
    <ModulesContext.Provider value={{ 
      modules, 
      loading, 
      isModuleEnabled, 
      refreshModules 
    }}>
      {children}
    </ModulesContext.Provider>
  );
};

export const useModules = () => {
  const context = useContext(ModulesContext);
  if (!context) {
    throw new Error('useModules must be used within a ModulesProvider');
  }
  return context;
};

export default ModulesContext;
